import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_ITEMS } from "../lib/constants";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="Medstry" className="h-12 md:h-16 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => {
            const isAnchor = item.href.includes("#");
            const isExternal = item.href.startsWith("/") && !isAnchor;
            return isExternal ? (
              <Link
                key={item.label}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  pathname === item.href ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contact"
            data-testid="nav-cta-request"
            className="inline-flex items-center gap-1.5 btn-primary px-3 sm:px-5 py-2.5 text-xs sm:text-sm font-semibold whitespace-nowrap"
          >
            <span className="hidden sm:inline">Book a Free Consultation</span>
            <span className="sm:hidden">Book Consultation</span>
            <ArrowUpRight className="h-4 w-4 hidden sm:inline" strokeWidth={2} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center text-slate-900"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setOpen(false)}
                className="px-2 py-3 text-base font-medium text-slate-700 border-b border-slate-100"
              >
                {item.label}
              </a>
            ))}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              className="mt-3 btn-primary px-5 py-3 text-sm font-semibold text-center"
            >
              Book a Free Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
